'use client';

import { Checkbox as CheckboxPrimitive } from '@base-ui/react/checkbox';
import { cva } from 'class-variance-authority';
import { Minus } from 'lucide-react';
import * as React from 'react';
import {
  type FieldProps,
  FieldTooltip,
  hasFieldProps,
  splitFieldProps,
} from '@/components/atoms/forms/_shared';
import {
  Field,
  FieldBottom,
  FieldDescription,
  FieldError,
  FieldLabel,
  useFieldContextOptional,
  useFieldControlProps,
} from '@/components/ui/form-field';
import { DrawCheck } from '@/components/ui/selection-group';
import { cn } from '@/lib/utils';

// ============================================
// CVA Variants
// ============================================

const checkboxVariants = cva(
  // Base styles
  'relative flex shrink-0 items-center justify-center border shadow-xs transition-[border-color,background-color,color,box-shadow] duration-(--duration-fast) ease-standard motion-reduce:transition-none focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: '',
        secondary: '',
        info: '',
        success: '',
        warning: '',
        destructive: '',
      },
      size: {
        sm: 'size-4 rounded-[4px]',
        md: 'size-5 rounded-[5px]',
        lg: 'size-6 rounded-[6px]',
      },
      checked: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      // ===== UNCHECKED (all variants) =====
      {
        checked: false,
        className:
          'bg-transparent border-input not-data-disabled:hover:border-primary not-data-disabled:hover:bg-primary/10',
      },
      // ===== PRIMARY CHECKED =====
      {
        variant: 'primary',
        checked: true,
        className:
          'border-primary bg-primary text-primary-foreground not-data-disabled:hover:bg-primary/90',
      },
      // ===== SECONDARY CHECKED (outline style) =====
      {
        variant: 'secondary',
        checked: true,
        className:
          'border-secondary bg-secondary text-secondary-foreground not-data-disabled:hover:bg-secondary/90',
      },
      // ===== INFO CHECKED =====
      {
        variant: 'info',
        checked: true,
        className:
          'border-info bg-info text-info-foreground not-data-disabled:hover:bg-info/90',
      },
      // ===== SUCCESS CHECKED =====
      {
        variant: 'success',
        checked: true,
        className:
          'border-success bg-success text-success-foreground not-data-disabled:hover:bg-success/90',
      },
      // ===== WARNING CHECKED =====
      {
        variant: 'warning',
        checked: true,
        className:
          'border-warning bg-warning text-warning-foreground not-data-disabled:hover:bg-warning/90',
      },
      // ===== DESTRUCTIVE CHECKED =====
      {
        variant: 'destructive',
        checked: true,
        className:
          'border-destructive bg-destructive text-destructive-foreground not-data-disabled:hover:bg-destructive/90',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'sm',
      checked: false,
    },
  },
);

// ============================================
// Types
// ============================================

type CheckboxVariant =
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'destructive';
type CheckboxSize = 'sm' | 'md' | 'lg';

/**
 * Checkbox is a pure atom component that renders ONLY the checkbox element.
 * It does NOT render label, description, or error.
 *
 * Supports 6 color variants and 3 sizes matching Figma design.
 * When used inside a Field with an error, automatically uses destructive variant.
 *
 * @example
 * ```tsx
 * // Simple checkbox
 * <Checkbox />
 *
 * // With variant and size
 * <Checkbox variant="success" size="lg" />
 *
 * // Auto-destructive when Field has error
 * <Field error="Required">
 *   <Checkbox />  // automatically uses destructive variant
 * </Field>
 * ```
 */
type CheckboxControlProps = Omit<
  CheckboxPrimitive.Root.Props,
  'onCheckedChange'
> & {
  /** Color variant for the checkbox */
  variant?: CheckboxVariant;
  /** Size variant for the checkbox */
  size?: CheckboxSize;
  /** Controlled checked state. When provided, component is controlled. */
  checked?: boolean;
  /** Initial checked state for uncontrolled mode */
  defaultChecked?: boolean;
  /** Callback when checked state changes */
  onCheckedChange?: (checked: boolean) => void;
};

export interface CheckboxProps
  extends CheckboxControlProps,
    Omit<FieldProps, 'topRight' | 'bottomRight'> {}

export type { CheckboxSize, CheckboxVariant };

// ============================================
// Size configuration
// ============================================

const iconClasses = {
  sm: 'size-3',
  md: 'size-3.5',
  lg: 'size-4',
};

// ============================================
// Checkbox (pure atom)
// ============================================

const CheckboxControl = React.forwardRef<
  HTMLButtonElement,
  CheckboxControlProps
>(
  (
    {
      className,
      variant: variantProp,
      size = 'sm',
      checked: controlledChecked,
      defaultChecked = false,
      onCheckedChange,
      ...props
    },
    ref,
  ) => {
    // Internal state for uncontrolled mode
    const [internalChecked, setInternalChecked] =
      React.useState(defaultChecked);

    // Use controlled value if provided, otherwise use internal state
    const isControlled = controlledChecked !== undefined;
    const checked = isControlled ? controlledChecked : internalChecked;
    const selected = checked || props.indeterminate;

    // Field context integration - auto-destructive on error
    const fieldContext = useFieldContextOptional();
    const fieldControlProps = useFieldControlProps();
    const hasFieldError = fieldContext?.state === 'destructive';
    const variant = variantProp ?? (hasFieldError ? 'destructive' : 'primary');

    const handleCheckedChange = React.useCallback(
      (newChecked: boolean) => {
        if (!isControlled) {
          setInternalChecked(newChecked);
        }
        onCheckedChange?.(newChecked);
      },
      [isControlled, onCheckedChange],
    );

    return (
      <CheckboxPrimitive.Root
        ref={ref}
        data-slot="checkbox"
        data-size={size}
        data-variant={variant}
        id={fieldControlProps.id}
        aria-invalid={fieldControlProps['aria-invalid']}
        aria-describedby={fieldControlProps['aria-describedby']}
        checked={checked}
        onCheckedChange={handleCheckedChange}
        className={cn(
          checkboxVariants({ variant, size, checked: selected }),
          // Touch target expansion (ensures ≥24px hit area)
          'before:content-[""] before:absolute before:-inset-[4px] before:rounded-[inherit]',
          // Focus state - double ring effect
          'focus-visible:ring-4 focus-visible:ring-bg-secondary focus-visible:shadow-[0_0_0_2px_var(--card),0_0_0_4px_var(--border)]',
          'dark:focus-visible:ring-bg-tertiary dark:focus-visible:shadow-[0_0_0_2px_var(--card),0_0_0_4px_var(--border)]',
          className,
        )}
        {...props}
      >
        <CheckboxPrimitive.Indicator
          data-slot="checkbox-indicator"
          className="flex items-center justify-center text-current data-[ending-style]:invisible"
        >
          {props.indeterminate ? (
            <Minus className={iconClasses[size]} strokeWidth={3} />
          ) : (
            <DrawCheck className={iconClasses[size]} />
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    );
  },
);

CheckboxControl.displayName = 'CheckboxControl';

// =============================================================================
// Checkbox — public API with smart field mode (inline layout)
// =============================================================================

const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProps>(
  (props, forwardedRef) => {
    const [fieldPropsObj, controlProps] = splitFieldProps(props);
    const { ref: _ref, ...cp } = controlProps as CheckboxControlProps & {
      ref?: unknown;
    };

    if (!hasFieldProps(fieldPropsObj)) {
      return (
        <CheckboxControl
          ref={forwardedRef}
          {...cp}
          required={fieldPropsObj.required}
        />
      );
    }

    const {
      label,
      required,
      labelIcon,
      tooltip,
      description,
      error,
      fieldClassName,
    } = fieldPropsObj;

    return (
      <Field
        layout="inline"
        state={error ? 'destructive' : undefined}
        disabled={cp.disabled}
        className={fieldClassName}
      >
        <CheckboxControl ref={forwardedRef} {...cp} required={required} />
        {label && (
          <FieldLabel required={required} labelIcon={labelIcon}>
            {label}
            {tooltip && <FieldTooltip content={tooltip} />}
          </FieldLabel>
        )}
        {(error || description) && (
          <FieldBottom>
            {error && <FieldError>{error}</FieldError>}
            {!error && description && (
              <FieldDescription>{description}</FieldDescription>
            )}
          </FieldBottom>
        )}
      </Field>
    );
  },
);
Checkbox.displayName = 'Checkbox';

export { Checkbox };
